import { Component, OnInit, ViewChild, signal, computed, inject, viewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoclassifierSdkService } from '../../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { DocumentManagementService } from '../../../services/document-management/document-management.service';
import { DocumentViewerComponent } from '../../../components/document-viewer/document-viewer.component';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TranscriptMetadataService } from '../../../services/transcript-metadata/transcript-metadata.service';
import { TranscriptMetadata, mapToTranscriptMetadata } from '../../../models/documentResponse';
import { Document } from '../../../models/document';
import { AuthService } from '../../../services/auth/auth.service';
import { ItpExtractionDataComponent } from '../components/itp-extraction-data/itp-extraction-data.component';
import { Bind } from 'primeng/bind';
import { Splitter } from 'primeng/splitter';
import { PrimeTemplate } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { StatusConfig } from '../../../constants';

@Component({
  selector: 'app-transcript-data',
  templateUrl: './transcript-data.component.html',
  styleUrls: ['./transcript-data.component.scss'],
  imports: [
    Bind,
    Splitter,
    PrimeTemplate,
    DocumentViewerComponent,
    ItpExtractionDataComponent,
    ConfirmDialog,
    TranslatePipe,
    Button,
    Tooltip,
    NgClass
  ]
})
export class TranscriptDataComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private docMngmtService = inject(DocumentManagementService);
  private translateService = inject(TranslateService);
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private authService = inject(AuthService);

  @ViewChild(ItpExtractionDataComponent) extractionDataComponent?: ItpExtractionDataComponent;
  private readonly extractionData = viewChild(ItpExtractionDataComponent);
  @ViewChild('documentViewer', { static: false }) documentViewer!: DocumentViewerComponent;

  transcriptStatus = signal<string>('');
  isTranscriptProcessing = computed(() => this.transcriptStatus() === '');

  documentId: string = '';
  transcriptId: string = '';

  documents = signal<TranscriptMetadata[]>([]);
  showUnsupportedFileMessage = signal(false);
  currentDocumentBase64Content = signal<string>('');
  documentLoadError = signal(false);
  currentDocument = computed(() =>
    this.documents().find(doc => doc.documentId === this.documentId)
  );

  showStatusButton = computed(() => {
    const extraction = this.extractionData();
    return !!extraction && !extraction.isExtracting() && !extraction.dataLoadError();
  });
  statusTranslationKey = computed(() => this.getStatusEntry().translationKey);
  statusClass = computed(() => this.getStatusEntry().cssClass);
  isExporting = computed(() => this.extractionData()?.isExporting() ?? false);

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initializeDocumentLoading();
  }

  /**
   * Navigates back to the transcript queue.
   */
  async onBack(): Promise<void> {
    const extraction = this.extractionData();
    if (extraction) {
      await extraction.onClose();
    } else {
      await this.router.navigate(['transcripts']);
    }
  }

  /**
   * Opens the overall confidence reasoning modal in the extraction data panel.
   */
  onStatusClick(): void {
    this.extractionData()?.showOverallConfidenceModal();
  }

  /**
   * Resolves the status config entry for the current transcript status.
   */
  private getStatusEntry(): { translationKey: string; cssClass: string } {
    const statusId = this.extractionData()?.transcriptStatusId() ?? null;
    return (
      StatusConfig.get(statusId ?? -1) ?? {
        translationKey: 'transcriptData.defaultStatus',
        cssClass: 'status-default'
      }
    );
  }

  /**
   * Handles changes to the lock state of a transcript document.
   * @param event An object containing the transcriptId and the user who has locked the document.
   * @param event.transcriptId The ID of the transcript whose lock state has changed.
   * @param event.lockedByUser The user who has locked the transcript, or null if unlocked.
   */
  onLockStateChanged(event: { transcriptId: string; lockedByUser: string | null }): void {
    this.documents.update(docs =>
      docs.map(doc =>
        doc.transId === event.transcriptId ? { ...doc, lockedByUser: event.lockedByUser } : doc
      )
    );
  }

  /**
   *  Handles changes to the transcript processing status.
   * @param status The new status of the transcript processing.
   */
  onTranscriptStatusChanged(status: string): void {
    this.transcriptStatus.set(status);
  }

  /**
   * Initializes document loading based on route parameters
   */
  private initializeDocumentLoading(): void {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.transcriptId = params['id'];
        this.transcriptMetadataService
          .getTranscriptMetadata(this.transcriptId)
          .then(doc => {
            this.documents.set([mapToTranscriptMetadata(doc)]);
            this.documentId = doc.documentId;
            this.loadDocument();
          })
          .catch(error => {});
      }
    });
  }

  /**
   * Loads the document content (PDF)
   */
  loadDocument(): void {
    this.getDocumentContent(this.documentId)
      .then(documentContent => {
        this.currentDocumentBase64Content.set(documentContent);
      })
      .catch(error => {
        console.error('Failed to load document:', error);
        this.documentLoadError.set(true);
      });
  }

  /**
   * Retrieves the base64 content of a document using presigned URL from transcript metadata
   * @param documentId The unique identifier of the document to retrieve.
   */
  getDocumentContent(documentId: string): Promise<string> {
    const doc = this.currentDocument();

    if (!doc?.documentUrl) {
      throw new Error('No document URL available');
    }

    return firstValueFrom(this.docMngmtService.getDocument(doc.documentUrl)).then(arrayBuffer => {
      const binaryString = Array.from(new Uint8Array(arrayBuffer))
        .map(byte => String.fromCharCode(byte))
        .join('');
      return btoa(binaryString);
    });
  }
}
